import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useState, type FormEvent } from "react";
import { Button } from "@/shared/components/Button";
import { combinarDataHora, separarDataHora } from "@/shared/lib/datas";
import { CamposEscala, type ValoresEscala } from "@/modules/escala/components/CamposEscala";
import type { Escala } from "@/modules/escala/services/escalaService";

interface FormularioEscalaProps {
  escalaEmEdicao: Escala;
  carregando: boolean;
  erro: string | null;
  onSalvar: (dados: { nome: string; descricao: string | null; dataHoraISO: string }) => void;
  onCancelar: () => void;
}

/**
 * Edição de uma escala já criada (modal): nome, descrição e data/hora. O
 * ministério e o "exigir confirmação" ficam de fora — são imutáveis depois da
 * criação. A criação mora na página "Nova escala" (`NovaEscalaPage`), que usa
 * os mesmos campos (`CamposEscala`).
 */
export function FormularioEscala({ escalaEmEdicao, carregando, erro, onSalvar, onCancelar }: FormularioEscalaProps) {
  const [valores, setValores] = useState<ValoresEscala>({
    ministerioId: escalaEmEdicao.ministerio_id,
    nome: escalaEmEdicao.nome,
    descricao: escalaEmEdicao.descricao ?? "",
    ...separarDataHora(escalaEmEdicao.data_hora),
    exigeConfirmacao: escalaEmEdicao.exige_confirmacao,
  });

  function atualizar<K extends keyof ValoresEscala>(campo: K, valor: ValoresEscala[K]) {
    setValores((v) => ({ ...v, [campo]: valor }));
  }

  function aoSubmeter(e: FormEvent) {
    e.preventDefault();
    if (!valores.nome.trim() || !valores.data || !valores.hora) return;
    onSalvar({
      nome: valores.nome.trim(),
      descricao: valores.descricao.trim() ? valores.descricao.trim() : null,
      dataHoraISO: combinarDataHora(valores.data, valores.hora),
    });
  }

  return (
    <form onSubmit={aoSubmeter} className="flex flex-col gap-4">
      <CamposEscala valores={valores} onAtualizar={atualizar} />

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" carregando={carregando} className="flex-1">
          Salvar alterações
        </Button>
        <BotaoVoltar onClick={onCancelar} disabled={carregando} className="flex-1" />
      </div>
    </form>
  );
}
